import React, { useEffect, useMemo, useState } from "react";
import { AnimatePresence, motion } from "framer-motion";
import {
  useDeleteContactMutation,
  useGetContactByIdQuery,
  useGetContactsQuery,
  useUpdateContactStatusMutation,
} from "../../app/api/contactApi";
import type { ContactStatus } from "../../app/api/contactApi";

const STATUSES: ContactStatus[] = ["new", "read", "replied", "archived"];

const STATUS_STYLES: Record<ContactStatus, string> = {
  new: "bg-itl-blue/10 text-itl-blue",
  read: "bg-amber-100 text-amber-700",
  replied: "bg-emerald-100 text-emerald-700",
  archived: "bg-gray-100 text-gray-500",
};

const PAGE_SIZE = 20;

function formatDate(value: string) {
  return new Date(value).toLocaleString("en-NG", {
    timeZone: "Africa/Lagos",
    dateStyle: "medium",
    timeStyle: "short",
  });
}

function StatusPill({ status }: { status: ContactStatus }) {
  return (
    <span
      className={`inline-block text-[10px] font-semibold uppercase tracking-[0.14em] px-2.5 py-1 ${STATUS_STYLES[status]}`}
    >
      {status}
    </span>
  );
}

/* ── Count tile: one cheap query per status ─────────────────────────── */
function CountTile({
  label,
  status,
  active,
  onClick,
}: {
  label: string;
  status?: ContactStatus;
  active: boolean;
  onClick: () => void;
}) {
  const { data } = useGetContactsQuery({ status, page: 1, limit: 1 });

  return (
    <button
      onClick={onClick}
      className={`text-left bg-white border px-5 py-4 transition-colors duration-200 ${
        active
          ? "border-itl-navy"
          : "border-gray-100 hover:border-gray-300"
      }`}
    >
      <div className="text-[10px] font-semibold uppercase tracking-[0.18em] text-gray-400">
        {label}
      </div>
      <div className="mt-2 font-serif text-[26px] leading-none">
        {data?.pagination.total ?? "—"}
      </div>
    </button>
  );
}

/* ── Detail drawer ─────────────────────────────────────────────────── */
function BriefDetail({
  id,
  onClose,
}: {
  id: string;
  onClose: () => void;
}) {
  const { data, isLoading, isError } = useGetContactByIdQuery(id);
  const [updateStatus, { isLoading: isSaving }] =
    useUpdateContactStatusMutation();
  const [deleteContact, { isLoading: isDeleting }] = useDeleteContactMutation();

  const [notes, setNotes] = useState("");
  const [saved, setSaved] = useState(false);
  const [confirmDelete, setConfirmDelete] = useState(false);

  const contact = data?.data;

  useEffect(() => {
    setNotes(contact?.notes ?? "");
    setSaved(false);
    setConfirmDelete(false);
  }, [contact?.id, contact?.notes]);

  async function handleStatus(status: ContactStatus) {
    if (!contact) return;
    await updateStatus({ id: contact.id, status, notes }).unwrap();
    setSaved(true);
  }

  async function handleSaveNotes() {
    if (!contact) return;
    await updateStatus({ id: contact.id, status: contact.status, notes })
      .unwrap()
      .catch(() => undefined);
    setSaved(true);
  }

  async function handleDelete() {
    if (!contact) return;
    await deleteContact(contact.id).unwrap().catch(() => undefined);
    onClose();
  }

  return (
    <motion.aside
      initial={{ x: "100%" }}
      animate={{ x: 0 }}
      exit={{ x: "100%" }}
      transition={{ type: "tween", duration: 0.28, ease: "easeOut" }}
      className="fixed top-0 right-0 bottom-0 z-50 w-full sm:w-[520px] bg-white shadow-2xl overflow-y-auto"
    >
      <div className="sticky top-0 bg-white border-b border-gray-100 px-6 sm:px-8 h-[68px] flex items-center justify-between">
        <span className="text-[11px] font-semibold uppercase tracking-[0.2em] text-gray-400">
          Project Brief
        </span>
        <button
          onClick={onClose}
          className="text-[11px] font-semibold uppercase tracking-[0.16em] text-gray-400 hover:text-itl-navy transition-colors"
        >
          Close ✕
        </button>
      </div>

      <div className="px-6 sm:px-8 py-8">
        {isLoading && (
          <p className="text-[14px] text-gray-400">Loading brief…</p>
        )}
        {isError && (
          <p className="text-[14px] text-red-600">
            Could not load this brief. It may have been deleted.
          </p>
        )}

        {contact && (
          <>
            <StatusPill status={contact.status} />
            <h2 className="mt-4 font-serif text-[26px] leading-tight">
              {contact.name}
            </h2>
            {contact.company && (
              <p className="mt-1 text-[14px] text-gray-500">
                {contact.company}
              </p>
            )}
            <p className="mt-1 text-[12px] text-gray-400">
              Submitted {formatDate(contact.createdAt)}
            </p>

            <div className="mt-8 grid grid-cols-2 gap-x-6 gap-y-5">
              {[
                {
                  label: "Email",
                  value: contact.email,
                  href: `mailto:${contact.email}`,
                },
                {
                  label: "Phone",
                  value: contact.phone || "—",
                  href: contact.phone ? `tel:${contact.phone}` : undefined,
                },
                { label: "Project Type", value: contact.projectType || "—" },
                { label: "Budget", value: contact.budget || "—" },
                { label: "Timeline", value: contact.timeline || "—" },
                {
                  label: "Confirmation Email",
                  value: contact.confirmationSent ? "Sent" : "Not sent",
                },
              ].map((field) => (
                <div key={field.label}>
                  <div className="text-[10px] font-semibold uppercase tracking-[0.18em] text-gray-400">
                    {field.label}
                  </div>
                  {field.href ? (
                    <a
                      href={field.href}
                      className="mt-1 block text-[14px] text-itl-blue hover:underline break-words"
                    >
                      {field.value}
                    </a>
                  ) : (
                    <div className="mt-1 text-[14px] break-words">
                      {field.value}
                    </div>
                  )}
                </div>
              ))}
            </div>

            <div className="mt-8">
              <div className="text-[10px] font-semibold uppercase tracking-[0.18em] text-gray-400">
                Brief
              </div>
              <p className="mt-2 text-[14px] leading-relaxed whitespace-pre-wrap">
                {contact.message}
              </p>
            </div>

            <div className="mt-8">
              <div className="text-[10px] font-semibold uppercase tracking-[0.18em] text-gray-400">
                Internal Notes
              </div>
              <textarea
                rows={4}
                value={notes}
                onChange={(e) => {
                  setNotes(e.target.value);
                  setSaved(false);
                }}
                placeholder="Add context for the team — call outcomes, scope, next steps…"
                className="mt-2 w-full border border-gray-200 p-3 text-[14px] focus:outline-none focus:border-itl-navy transition-colors"
              />
              <button
                onClick={handleSaveNotes}
                disabled={isSaving}
                className="mt-2 text-[11px] font-semibold uppercase tracking-[0.16em] text-itl-blue hover:text-itl-navy transition-colors disabled:opacity-50"
              >
                {isSaving ? "Saving…" : saved ? "Saved ✓" : "Save notes"}
              </button>
            </div>

            <div className="mt-10 pt-8 border-t border-gray-100">
              <div className="text-[10px] font-semibold uppercase tracking-[0.18em] text-gray-400">
                Move to
              </div>
              <div className="mt-3 flex flex-wrap gap-2">
                {STATUSES.map((status) => (
                  <button
                    key={status}
                    onClick={() => handleStatus(status)}
                    disabled={isSaving || status === contact.status}
                    className={`text-[11px] font-semibold uppercase tracking-[0.14em] px-4 py-2 border transition-colors duration-200 ${
                      status === contact.status
                        ? "border-itl-navy bg-itl-navy text-white"
                        : "border-gray-200 hover:border-itl-navy"
                    }`}
                  >
                    {status}
                  </button>
                ))}
              </div>

              <div className="mt-8 flex items-center gap-4">
                <a
                  href={`mailto:${contact.email}?subject=${encodeURIComponent(
                    `Re: your project brief — Incubator Tech`,
                  )}`}
                  className="bg-itl-navy text-white text-[11px] font-semibold uppercase tracking-[0.16em] px-6 py-3 hover:bg-itl-blue transition-colors duration-200"
                >
                  Reply by email
                </a>

                {confirmDelete ? (
                  <div className="flex items-center gap-3">
                    <button
                      onClick={handleDelete}
                      disabled={isDeleting}
                      className="text-[11px] font-semibold uppercase tracking-[0.16em] text-red-600 hover:underline disabled:opacity-50"
                    >
                      {isDeleting ? "Deleting…" : "Confirm delete"}
                    </button>
                    <button
                      onClick={() => setConfirmDelete(false)}
                      className="text-[11px] uppercase tracking-[0.16em] text-gray-400 hover:text-itl-navy"
                    >
                      Cancel
                    </button>
                  </div>
                ) : (
                  <button
                    onClick={() => setConfirmDelete(true)}
                    className="text-[11px] font-semibold uppercase tracking-[0.16em] text-gray-400 hover:text-red-600 transition-colors"
                  >
                    Delete
                  </button>
                )}
              </div>
            </div>
          </>
        )}
      </div>
    </motion.aside>
  );
}

/* ── Page ──────────────────────────────────────────────────────────── */
export default function AdminBriefs() {
  const [status, setStatus] = useState<ContactStatus | undefined>(undefined);
  const [searchInput, setSearchInput] = useState("");
  const [search, setSearch] = useState("");
  const [page, setPage] = useState(1);
  const [selectedId, setSelectedId] = useState<string | null>(null);

  // Debounce the search box so every keystroke isn't a request
  useEffect(() => {
    const timer = setTimeout(() => {
      setSearch(searchInput.trim());
      setPage(1);
    }, 350);
    return () => clearTimeout(timer);
  }, [searchInput]);

  const { data, isLoading, isFetching, isError, refetch } = useGetContactsQuery(
    {
      status,
      search: search || undefined,
      page,
      limit: PAGE_SIZE,
    },
  );

  const contacts = useMemo(() => data?.data ?? [], [data]);
  const pagination = data?.pagination;

  return (
    <div>
      <div className="flex flex-wrap items-end justify-between gap-4">
        <div>
          <p className="text-[11px] font-semibold uppercase tracking-[0.2em] text-itl-blue">
            Inbox
          </p>
          <h1 className="mt-2 font-serif text-[32px] leading-tight">
            Project Briefs
          </h1>
          <p className="mt-2 text-[14px] text-gray-500">
            Every enquiry and project brief submitted through the site.
          </p>
        </div>

        <button
          onClick={() => refetch()}
          className="text-[11px] font-semibold uppercase tracking-[0.16em] text-gray-500 hover:text-itl-navy border border-gray-200 hover:border-itl-navy px-4 py-2.5 transition-colors duration-200"
        >
          {isFetching ? "Refreshing…" : "Refresh"}
        </button>
      </div>

      {/* Counts double as status filters */}
      <div className="mt-8 grid grid-cols-2 sm:grid-cols-3 lg:grid-cols-5 gap-3">
        <CountTile
          label="All"
          active={status === undefined}
          onClick={() => {
            setStatus(undefined);
            setPage(1);
          }}
        />
        {STATUSES.map((s) => (
          <CountTile
            key={s}
            label={s}
            status={s}
            active={status === s}
            onClick={() => {
              setStatus(s);
              setPage(1);
            }}
          />
        ))}
      </div>

      <div className="mt-6">
        <input
          value={searchInput}
          onChange={(e) => setSearchInput(e.target.value)}
          placeholder="Search by name, email or company…"
          className="w-full sm:max-w-sm bg-white border border-gray-200 px-4 py-3 text-[14px] placeholder-gray-300 focus:outline-none focus:border-itl-navy transition-colors"
        />
      </div>

      <div className="mt-6 bg-white border border-gray-100">
        {isLoading ? (
          <p className="px-6 py-16 text-center text-[14px] text-gray-400">
            Loading briefs…
          </p>
        ) : isError ? (
          <p className="px-6 py-16 text-center text-[14px] text-red-600">
            Could not load briefs. Check that the API is reachable and that you
            are still signed in.
          </p>
        ) : contacts.length === 0 ? (
          <p className="px-6 py-16 text-center text-[14px] text-gray-400">
            No briefs {status ? `marked "${status}"` : "yet"}.
          </p>
        ) : (
          <div className="overflow-x-auto">
            <table className="w-full min-w-[720px] text-left">
              <thead>
                <tr className="border-b border-gray-100">
                  {["Name", "Project", "Budget", "Received", "Status"].map(
                    (heading) => (
                      <th
                        key={heading}
                        className="px-6 py-4 text-[10px] font-semibold uppercase tracking-[0.18em] text-gray-400"
                      >
                        {heading}
                      </th>
                    ),
                  )}
                </tr>
              </thead>
              <tbody>
                {contacts.map((contact) => (
                  <tr
                    key={contact.id}
                    onClick={() => setSelectedId(contact.id)}
                    className="border-b border-gray-50 last:border-0 hover:bg-itl-cream/60 cursor-pointer transition-colors"
                  >
                    <td className="px-6 py-4">
                      <div className="text-[14px] font-medium">
                        {contact.name}
                      </div>
                      <div className="text-[12px] text-gray-400">
                        {contact.company || contact.email}
                      </div>
                    </td>
                    <td className="px-6 py-4 text-[13px] text-gray-600">
                      {contact.projectType || "—"}
                    </td>
                    <td className="px-6 py-4 text-[13px] text-gray-600">
                      {contact.budget || "—"}
                    </td>
                    <td className="px-6 py-4 text-[13px] text-gray-500 whitespace-nowrap">
                      {formatDate(contact.createdAt)}
                    </td>
                    <td className="px-6 py-4">
                      <StatusPill status={contact.status} />
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        )}
      </div>

      {pagination && pagination.pages > 1 && (
        <div className="mt-6 flex items-center justify-between">
          <p className="text-[12px] text-gray-400">
            Page {pagination.page} of {pagination.pages} · {pagination.total}{" "}
            total
          </p>
          <div className="flex gap-2">
            <button
              onClick={() => setPage((p) => Math.max(1, p - 1))}
              disabled={page <= 1}
              className="text-[11px] font-semibold uppercase tracking-[0.16em] border border-gray-200 px-4 py-2.5 hover:border-itl-navy disabled:opacity-40 disabled:hover:border-gray-200 transition-colors"
            >
              Previous
            </button>
            <button
              onClick={() =>
                setPage((p) => Math.min(pagination.pages, p + 1))
              }
              disabled={page >= pagination.pages}
              className="text-[11px] font-semibold uppercase tracking-[0.16em] border border-gray-200 px-4 py-2.5 hover:border-itl-navy disabled:opacity-40 disabled:hover:border-gray-200 transition-colors"
            >
              Next
            </button>
          </div>
        </div>
      )}

      <AnimatePresence>
        {selectedId && (
          <>
            <motion.div
              initial={{ opacity: 0 }}
              animate={{ opacity: 1 }}
              exit={{ opacity: 0 }}
              onClick={() => setSelectedId(null)}
              className="fixed inset-0 z-40 bg-itl-navy/30"
            />
            <BriefDetail
              key={selectedId}
              id={selectedId}
              onClose={() => setSelectedId(null)}
            />
          </>
        )}
      </AnimatePresence>
    </div>
  );
}
